import React, { useState, useEffect, useRef, memo } from "react";
import "./storecard.scss";
import random from "random";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../__redux/slices/StoreSlice";
import Button from "./Button/Button";

// Global cache to prevent multiple loads of the same image
const imageLoadCache = new Set();

const StoreCard = memo(({ index, productInfo, onCardClick }) => {
  const [rotation, setRotation] = React.useState(0);
  const [scale, setScale] = React.useState(1);
  const [bottomPadding, setBottomPadding] = useState("15px");
  const [showOverlay, setShowOverlay] = useState(false);
  // const [buttonScale, setButtonScale] = useState(1);
  const [selectedSize, setSelectedSize] = useState(0); // Index of selected size
  const isMobile = useSelector((state) => state.appSlice.isMobile);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef(null);

  const titleRef = useRef(null);
  const dispatch = useDispatch();
  useEffect(() => {
    if (titleRef.current) {
      const height = titleRef.current.clientHeight;
      // Assuming that one line height is 24px, for example
      if (height > 24) {
        // If the height is more than 24px, then it's likely two lines
        setBottomPadding("2px"); // Adjust this value as needed
      }
    }
  }, [productInfo.title]);

  // Simplified image loading - set image source directly
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && imageRef.current) {
            // Load the actual image
            imageRef.current.src = productInfo.imagePath;
          }
        });
      },
      { rootMargin: "50px" },
    );

    // Set image source directly and let the img element handle loading
    if (imageRef.current) {
      imageRef.current.src = productInfo.imagePath;
    }
  }, [productInfo.imagePath, productInfo.title]);

  const handleMouseEnter = () => {
    // Generate a random degree between -20 and 20
    const randomDegree = random.boolean()
      ? random.int(5, 10)
      : random.int(-5, -10);
    setRotation(randomDegree);
    setScale(1.1);
  };
  //rotate the image that amount of degrees when mousing over
  const imageStyle = {
    transition: "transform 0.3s ease-in-out",
    transform: `scale(${scale}) rotate(${rotation}deg)`,
  };
  const handleMouseLeave = () => {
    setScale(1.0);
    setRotation(0);
  };

  // Get current size details
  const currentSize = productInfo.availableSizes
    ? productInfo.availableSizes[selectedSize]
    : null;
  const isCurrentSizeInStock = currentSize
    ? currentSize.inStock
    : productInfo.inStock;

  const handleSizeChange = (event) => {
    event.stopPropagation();
    const newSelectedSize = parseInt(event.target.value);
    setSelectedSize(newSelectedSize);
  };

  const handleAddToCart = (event) => {
    event.stopPropagation();

    // setButtonScale(0.95);
    // setTimeout(() => setButtonScale(1), 150);

    // Get the current size directly from the selected index
    const selectedSizeData = productInfo.availableSizes
      ? productInfo.availableSizes[selectedSize]
      : null;

    const productDataForCart = {
      title: productInfo.title,
      price: selectedSizeData ? selectedSizeData.price : productInfo.price,
      imagePath: productInfo.imagePath,
      numInCart: 1,
      size: selectedSizeData ? selectedSizeData.size : productInfo.size,
      form: selectedSizeData ? selectedSizeData.form : productInfo.form,
      key: Math.random() * 11,
    };

    dispatch(addToCart(productDataForCart));

    setShowOverlay(true);
    setTimeout(() => setShowOverlay(false), 2500);
  };

  const handleImageError = (e) => {
    // Prevent infinite loop by checking if we're already trying to load the fallback
    setImageLoaded(true);
    return;
  };

  return (
    <div
      key={index}
      className="product-card"
      onClick={() => onCardClick(productInfo)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showOverlay && (
        <div className="overlay">
          <span className="overlay-text">Added to Cart!</span>
        </div>
      )}
      <div className="img-container">
        <img
          ref={imageRef}
          style={imageStyle}
          src={productInfo.imagePath}
          onError={handleImageError}
          onLoad={() => setImageLoaded(true)}
          alt={productInfo.title}
          loading="lazy"
        />
      </div>
      <h3 ref={titleRef}>{productInfo.title}</h3>

      {/* Size selector - only show if multiple sizes available */}
      {productInfo.availableSizes && productInfo.availableSizes.length > 1 && (
        <div className="size-selector" onClick={(e) => e.stopPropagation()}>
          <select
            value={selectedSize}
            onChange={handleSizeChange}
            className="size-dropdown"
          >
            {productInfo.availableSizes.map((size, index) => (
              <option key={index} value={index}>
                {size.size} - ${size.price}
              </option>
            ))}
          </select>
        </div>
      )}

      <p className={isCurrentSizeInStock ? "available" : "unavailable"}>
        {isCurrentSizeInStock ? "In Stock" : "Unavailable"}
      </p>
      <div className="card-bot" style={{ marginTop: bottomPadding }}>
        <p className="price">
          ${currentSize ? currentSize.price : productInfo.price}
        </p>
        <Button
          variant="primary"
          className="add-to-cart-button"
          onClick={handleAddToCart}
          disabled={!isCurrentSizeInStock}
        >
          Add to Cart
        </Button>
      </div>
    </div>
  );
});
export default StoreCard;
